/**
 * GoalMomentum — gentle progress language on goal cards.
 * Shows % complete bar + projection. Milestone-aware copy.
 */

export function GoalMomentum({ goal }) {
  const target = goal.target_amount;
  const current = goal.supported_total || 0;

  // For goals without a target, only show milestone copy if 100% reached
  if (!target || target <= 0) {
    const milestones = goal.milestones_achieved || [];
    if (milestones.includes(100)) {
      return (
        <div className="mt-3" data-testid="goal-momentum">
          <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="goal-momentum-text">
            You did it
          </p>
        </div>
      );
    }
    return null;
  }

  const pct = Math.min(Math.round((current / target) * 100), 100);
  const hasProgress = current > 0;

  // Milestone-aware copy
  const milestones = goal.milestones_achieved || [];
  const highestMilestone = milestones.length > 0 ? Math.max(...milestones) : null;

  const MILESTONE_COPY = {
    25: "You've made a strong start",
    50: "You're halfway there",
    75: "This is taking shape",
    100: "You did it",
  };

  const momentumText =
    highestMilestone && MILESTONE_COPY[highestMilestone]
      ? MILESTONE_COPY[highestMilestone]
      : hasProgress
      ? "You're making steady progress"
      : "Getting started";

  // Projection
  const createdAt = goal.created_at ? new Date(goal.created_at) : null;
  let projectionText = null;

  if (hasProgress && createdAt && pct < 100) {
    const daysSinceCreated = Math.max(
      (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24),
      1
    );
    const ratePerDay = current / daysSinceCreated;
    const remaining = target - current;
    const daysToGoal = remaining / ratePerDay;
    const weeksToGoal = Math.ceil(daysToGoal / 7);

    if (weeksToGoal > 0 && weeksToGoal < 200 && daysSinceCreated >= 7) {
      const cycles =
        weeksToGoal <= 4
          ? `${weeksToGoal} week${weeksToGoal !== 1 ? "s" : ""}`
          : `${Math.ceil(weeksToGoal / 4)} month${Math.ceil(weeksToGoal / 4) !== 1 ? "s" : ""}`;
      projectionText = `At this pace, you'll reach this in ~${cycles}`;
    }
  }

  return (
    <div className="mt-3 space-y-1.5" data-testid="goal-momentum">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#1a4d3e]/65 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${pct}%` }}
            data-testid="goal-momentum-bar"
          />
        </div>
        <span
          className="text-[11px] text-muted-foreground tabular-nums w-8 text-right"
          data-testid="goal-momentum-pct"
        >
          {pct}%
        </span>
      </div>

      <p
        className="text-[11px] text-muted-foreground leading-relaxed"
        data-testid="goal-momentum-text"
      >
        {momentumText}
      </p>

      {projectionText && (
        <p className="text-[11px] text-[#1a4d3e]/70" data-testid="goal-momentum-projection">
          {projectionText}
        </p>
      )}
    </div>
  );
}
